import axios from 'axios';
import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import { toast } from '@/components';
import type { ReportWithEvents } from '@/types/report';

export interface ReportOrderMessages {
    success: string;
    stale: string;
    error: string;
}

export interface ReportOrderMutation {
    isPending: boolean;
    run: (
        messages: ReportOrderMessages,
        reorder: (currentReport: ReportWithEvents) => Promise<void>,
    ) => Promise<void>;
}

interface UseReportOrderMutationParams {
    report: ReportWithEvents | undefined;
    mutate: KeyedMutator<ReportWithEvents>;
}

// Event and prompt drags validate the same `Report.orderVersion`, so they share
// one gate: a move started while another is in flight would send the version it
// rendered with — the one the in-flight move is about to advance — and come
// back a spurious 409.
export function useReportOrderMutation({
    report,
    mutate,
}: UseReportOrderMutationParams): ReportOrderMutation {
    const [isPending, setIsPending] = useState(false);

    const reportFailure = async (
        messages: ReportOrderMessages,
        error: unknown,
    ) => {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
            toast.error(messages.stale);
            await mutate();
            return;
        }
        toast.error(messages.error);
    };

    const run = async (
        messages: ReportOrderMessages,
        reorder: (currentReport: ReportWithEvents) => Promise<void>,
    ) => {
        if (!report || isPending) {
            return;
        }
        setIsPending(true);
        try {
            await reorder(report);
            toast.success(messages.success);
        } catch (error) {
            await reportFailure(messages, error);
        } finally {
            setIsPending(false);
        }
    };

    return { isPending, run };
}
